import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface UserProfile {
  id: string;
  user_id: string;
  role: 'player' | 'organizer' | 'admin';
  full_name: string | null;
  email: string;
  phone: string | null;
  avatar_url: string | null;
  bio: string | null;
  location: string | null;
  preferred_sports: string[] | null;
  skill_level: string | null;
  wins: number;
  losses: number;
  matches_played: number;
  points: number;
  achievements: number;
  created_at: string;
  updated_at: string;
}

export function useUserProfile(userId?: string) {
  const { user: authUser } = useAuth();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Use provided userId or current user's id
  const targetUserId = userId || authUser?.id;

  const fetchProfile = async () => {
    if (!targetUserId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', targetUserId)
        .single();

      if (error) {
        // If no profile exists, it's not necessarily an error for viewing other users
        if (error.code === 'PGRST116') {
          setProfile(null);
          setError('Perfil no encontrado');
        } else {
          throw error;
        }
      } else {
        setProfile(data);
        setError(null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar perfil');
      setProfile(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfile();
  }, [targetUserId]);

  // Check if this is the current user's own profile
  const isOwnProfile = authUser?.id === targetUserId;

  return {
    profile,
    loading,
    error,
    refetch: fetchProfile,
    isOwnProfile
  };
}